import { __awaiter, __decorate } from "tslib";
import { KeyCode } from '@ag-grid-community/core';
import { css, html, observable } from '@microsoft/fast-element';
import { DesignToken, FoundationElement } from '@microsoft/fast-foundation';
import { logger } from '../utils';
/**
 * The AG Action Renderer element.
 * @public
 * @tagname %%prefix%%-grid-pro-action-renderer
 */
export class ActionRenderer extends FoundationElement {
    init(params) {
        if (!params)
            return;
        this.params = params;
        params.eGridCell.addEventListener('keydown', (event) => {
            if (event.key === KeyCode.ENTER) {
                this.clickHandler();
            }
        });
    }
    getGui() {
        return this;
    }
    refresh(params) {
        this.params = params;
        return true;
    }
    isDisabled(data) {
        var _a;
        if (typeof ((_a = this.params) === null || _a === void 0 ? void 0 : _a.isDisabled) === 'function') {
            try {
                return this.params.isDisabled(data);
            }
            catch (error) {
                logger.error('Error executing grid action cell renderer isDisabled callback:', error);
            }
        }
        return false;
    }
    clickHandler() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pendingAction = true;
            yield this.params.actionClick(this.params.data);
            this.pendingAction = false;
        });
    }
    get dataTestId() {
        var _a;
        if (this.params.dataTestId) {
            return this.params.dataTestId + ((_a = this.params.data[this.params.uniqueFieldName]) !== null && _a !== void 0 ? _a : '');
        }
        return undefined;
    }
}
__decorate([
    observable
], ActionRenderer.prototype, "params", void 0);
__decorate([
    observable
], ActionRenderer.prototype, "pendingAction", void 0);
const { create } = DesignToken;
/**
 * The base height multiplier for the action renderer.
 * @remarks Results in `--action-height-multiplier`. This is a DesignToken {@link https://www.fast.design/docs/design-systems/design-tokens/#what-is-a-design-token} value. Defaults to `0.6px`. May have other values depending on the design system or can be set to a different value.
 * @public
 */
export const actionHeightMultiplier = create('action-height-multiplier').withDefault('0.6px');
/**
 * The AG Action Renderer Styles.
 * @public
 */
export const agActionRendererStyles = css `
  .action-container {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
  }
  .action-renderer {
    align-self: center;
    display: flex;
    height: calc(
      (var(--base-height-multiplier) + var(--density)) * var(--design-unit) *
        var(--action-height-multiplier)
    );
  }
`;
/**
 * A function that returns a Foundation Action Renderer for configuring the component with a DesignSystem.
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-grid-pro-action-renderer\>
 */
export const foundationGridProActionRenderer = ActionRenderer.compose({
    baseName: 'grid-pro-action-renderer',
    styles: css `
    ${agActionRendererStyles}
  `,
    template: html `
    <div class="action-container" part="action-container">
      <foundation-button
        class="action-renderer"
        part="action-renderer"
        data-test-id="${(x) => x.dataTestId}"
        ?disabled=${(x) => { var _a; return x.pendingAction || x.isDisabled((_a = x.params) === null || _a === void 0 ? void 0 : _a.data); }}
        appearance="${(x) => x.params.appearance}"
        @click=${(x) => x.clickHandler()}">${(x) => x.params.actionName}</foundation-button>
    </div>`,
});
